import { NgClass } from '@angular/common';
import { Component, computed, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputErrorComponent } from '@common/components/input/input-error/input-error.component';
import { InputLabelComponent } from '@common/components/input/input-label/input-label.component';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { DateTimePipe } from '@common/pipes/datetime.pipe';
import { environment } from '@environments/environment.base';
import { TranslatePipe } from '@ngx-translate/core';
import { DateTimePickerComponent, DateTimePickerCustomMessagesComponent } from '@progress/kendo-angular-dateinputs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { BaseInputComponent } from '../base-input/base-input.component';
import { DateRangeInputConfig, DateTimeInputConfig } from '../input.type';

/**
 * Datetime input component.
 * @param format - The format of the date.
 * @param minDate - The minimum date.
 * @param maxDate - The maximum date.
 * @param disabledDates - The disabled dates.
 */
@Component({
    selector: 'app-datetime-input',
    templateUrl: './datetime-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: DatetimeInputComponent }],
    imports: [
        InputLabelComponent,
        DateTimePickerComponent,
        NgClass,
        TranslatePipe,
        FormsModule,
        DateTimePickerCustomMessagesComponent,
        InputErrorComponent,
        TooltipDirective,
        DateTimePipe
    ]
})
export class DatetimeInputComponent extends BaseInputComponent<DateTimeInput> {
    @Input() format: DateTimeInputConfig['format'];
    @Input() formatPlaceholder: DateRangeInputConfig['formatPlaceholder'] = {
        day: this.translateService.instant('day'),
        month: this.translateService.instant('month'),
        year: this.translateService.instant('year'),
        hour: this.translateService.instant('hour'),
        minute: this.translateService.instant('minute'),
        second: this.translateService.instant('second'),
        millisecond: this.translateService.instant('millisecond')
    };
    @Input() minDate: DateTimeInputConfig['minDate'];
    @Input() maxDate: DateTimeInputConfig['maxDate'];
    @Input() disabledDates: DateTimeInputConfig['disabledDates'];

    defaultDateFormat: string = environment.settings.appControl.dateFormat;
    defaultTimeFormat: string = environment.settings.appControl.timeFormat;
    defaultDateTimeFormat: string = `${this.defaultDateFormat} ${this.defaultTimeFormat}`;

    constructor() {
        super();
        this.getDefaultDateFormat();
    }

    dateValue = computed<Date>(() => {
        if (!this.value()) return null;
        if (this.value() instanceof Date) {
            return this.value() as Date;
        }
        const date = new Date(this.value());
        return isNaN(date.getTime()) ? null : date;
    });

    override onValueChange(value: DateTimeInput) {
        this.value.set(value);
        super.onValueChange(value);
    }

    private getDefaultDateFormat() {
        const settings = this.queryService.queryClient.getQueryData<Setting[]>(['settings']);
        if (!settings) return;
        const defaultDateFormat = settings.find(
            (setting: Setting) => setting.key === SettingKeyCodes.DateFormat
        )?.value;
        if (defaultDateFormat) this.defaultDateFormat = defaultDateFormat;
        const defaultTimeFormat = settings.find(
            (setting: Setting) => setting.key === SettingKeyCodes.TimeFormat
        )?.value;
        if (defaultTimeFormat) this.defaultTimeFormat = defaultTimeFormat;
        this.defaultDateTimeFormat = this.format = `${this.defaultDateFormat} ${this.defaultTimeFormat}`;
    }
}

type DateTimeInput = Date | string;
